<template>
  <div>
    <h3 style="margin-bottom: 20px; color: #33408a; font-weight: bold;">{{ $t('titleSelectionText') }}</h3>

    <el-form-item
      v-for="(level, index) in visibleLevels"
      :key="level.key"
      :label="$t(level.labelKey)"
    >
      <el-autocomplete
        :ref="`autocomplete_${level.key}`"
        popper-class="correspondence-autocomplete"
        v-model="inputs[level.key]"
        :fetch-suggestions="(q, cb) => querySearch(index, q, cb)"
        :placeholder="$t('search')"
        @select="item => handleSelect(index, item)"
        @focus="() => onFocus(level.key)"
        @blur="() => onBlur(index)"
        @input="value => onInput(index, value)"
        clearable
      >
        <template slot-scope="{ item }">
          <div class="link">{{ isGreek ? item[level.fieldGr] : item[level.fieldEn] }}</div>
        </template>
      </el-autocomplete>
    </el-form-item>

    <!-- Breadcrumb -->
    <div class="selection-path">
      <strong>{{ $t('titleText') }}:</strong>
      <br>
      <span>{{ displayTitle }}</span>
    </div>

    <!-- Validation error -->
    <div v-if="showIncompleteError" class="selection-error">
      <i class="el-icon-warning"></i>
      {{ $t('incompleteSelectionMsg') }}
    </div>

    <hr>
  </div>
</template>

<script>
const LEVEL_CONFIG = [
  {
    key: 'fieldOfStudy',
    labelKey: 'fieldOfStudyText',
    fieldGr: 'epistimes',
    fieldEn: 'epistimes_eng',
    childrenKey: 'level2',
    tcKey: 'fieldOfStudy',
  },
  {
    key: 'titleProgramme',
    labelKey: 'titleProgrammeText',
    fieldGr: 'ptyxia',
    fieldEn: 'ptyxia_eng',
    childrenKey: 'level3',
    tcKey: 'titleProgramme',
  },
  {
    key: 'specialization',
    labelKey: 'specializationText',
    fieldGr: 'eidikeyseis',
    fieldEn: 'eidikeyseis_eng',
    childrenKey: 'level4',
    tcKey: 'specialization',
  },
  {
    key: 'subSpecialization',
    labelKey: 'subSpecializationText',
    fieldGr: 'eidikeyseis_fourth_level',
    fieldEn: 'eidikeyseis_fourth_level_eng',
    childrenKey: null,
    tcKey: 'subSpecialization',
  },
]

export default {
  props: ['application', 'allDepartments', 'academicAreasAll', 'checkFormTitles', 'applicationFinalSaved'],

  data() {
    return {
      inputs: {
        fieldOfStudy: '',
        titleProgramme: '',
        specialization: '',
        subSpecialization: '',
      },

      selected: {
        fieldOfStudy: null,
        titleProgramme: null,
        specialization: null,
        subSpecialization: null,
      },

      level2Options: [],
      level3Options: [],
      level4Options: [],

      showIncompleteError: false,
    }
  },

  computed: {
    isGreek() {
      return this.$i18n.locale === 'el'
    },

    levelOptions() {
      return [
        this.academicAreasAll,
        this.level2Options,
        this.level3Options,
        this.level4Options,
      ]
    },

    visibleLevels() {
      return LEVEL_CONFIG.filter((level, index) =>
        index === 0 || this.levelOptions[index].length > 0
      )
    },

    isLegacyApplication() {
      const tc = this.application.titlesCorrespondence
      const hasUniversityDepartment = tc.universityDepartment?.id != null
      const hasNoNewFields = ['fieldOfStudy', 'titleProgramme', 'specialization', 'subSpecialization']
        .every(key => tc[key]?.id == null)
      return hasUniversityDepartment && hasNoNewFields
    },

    checkSelection() {
      if (this.applicationFinalSaved) return false
      if (this.application.applicationType !== 'recognition') return false
      if (this.application.degreeType !== 'bachelor') return false
      return true
    },

    isSelectionIncomplete() {
      if (!this.checkSelection) return false
      return LEVEL_CONFIG.some((level, index) => {
        const isFirstLevel = index === 0
        const hasOptions   = this.levelOptions[index].length > 0
        return (isFirstLevel || hasOptions) && !this.selected[level.key]
      })
    },

    selectionPathLocalized() {
      return this._buildPath(level => this.isGreek ? level.fieldGr : level.fieldEn)
    },

    displayTitle() {
      if (this.selectionPathLocalized) return this.selectionPathLocalized
      if (this.isLegacyApplication) {
        return this.application.titlesCorrespondence.universityDepartment.name
      }
      return '-'
    },
  },

  methods: {
    // ==========================================
    // HELPERS
    // ==========================================
    _buildPath(fieldGetter) {
      const parts = LEVEL_CONFIG
        .map(level => this.selected[level.key]?.[fieldGetter(level)])
        .filter(Boolean)
      return parts.length > 0 ? parts.join(' > ') : null
    },

    filterByTokens(source, fields, query) {
      if (!query) return [...source]
      const tokens = query.toLowerCase().split(/\s+/)
      return source.filter(item => {
        const searchText = fields.map(f => item[f] ?? '').join(' ').toLowerCase()
        return tokens.every(token => searchText.includes(token))
      })
    },

    findExactMatch(source, fields, inputVal) {
      return source.find(item =>
        fields.some(f => (item[f] ?? '').toLowerCase() === inputVal)
      )
    },

    _getFields(index) {
      const { fieldGr, fieldEn } = LEVEL_CONFIG[index]
      return [fieldGr, fieldEn]
    },

    _setChildOptions(index, options) {
      const optionsKeys = ['level2Options', 'level3Options', 'level4Options']
      const key = optionsKeys[index - 1]
      if (key) this[key] = options
    },

    _activateDropdown(key) {
      this.$nextTick(() => {
        const refValue = this.$refs[`autocomplete_${key}`]
        const ref = Array.isArray(refValue) ? refValue[0] : refValue
        if (ref) ref.activated = true
      })
    },

    // ==========================================
    // GENERIC LEVEL METHODS
    // ==========================================
    querySearch(index, queryString, cb) {
      const config   = LEVEL_CONFIG[index]
      const filtered = this.filterByTokens(
        this.levelOptions[index],
        this._getFields(index),
        queryString
      )
      filtered.sort((a, b) =>
        this.isGreek
          ? a[config.fieldGr].localeCompare(b[config.fieldGr], 'el')
          : a[config.fieldEn].localeCompare(b[config.fieldEn], 'en')
      )
      cb(filtered)
    },

    handleSelect(index, item) {
      const config = LEVEL_CONFIG[index]
      this.selected[config.key] = item
      this.inputs[config.key] = this.isGreek ? item[config.fieldGr] : item[config.fieldEn]
      if (config.childrenKey) {
        this._setChildOptions(index + 1, item[config.childrenKey] || [])
      }
      this.clearFrom(index + 1)
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    clearLevel(index) {
      const config = LEVEL_CONFIG[index]
      this.inputs[config.key] = ''
      this.selected[config.key] = null
      this._setChildOptions(index + 1, [])
      this.clearFrom(index + 1)
      this.syncTitlesCorrespondence()
      this.showIncompleteError = this.isSelectionIncomplete
      this.emitChanges()
    },

    clearFrom(startIndex) {
      for (let i = startIndex; i < LEVEL_CONFIG.length; i++) {
        const config = LEVEL_CONFIG[i]
        this.inputs[config.key] = ''
        this.selected[config.key] = null
        this._setChildOptions(i + 1, [])
      }
    },

    onFocus(key) {
      this._activateDropdown(key)
    },

    onBlur(index) {
      this.$nextTick(() => {
        const config = LEVEL_CONFIG[index]
        if (this.selected[config.key]) return

        const inputVal = this.inputs[config.key]?.trim().toLowerCase()
        if (!inputVal) {
          this.clearLevel(index)
          return
        }

        const source = this.levelOptions[index]
        const fields = this._getFields(index)

        const exactMatch = this.findExactMatch(source, fields, inputVal)
        if (exactMatch) {
          this.handleSelect(index, exactMatch)
          return
        }

        const partialMatches = this.filterByTokens(source, fields, inputVal)
        if (partialMatches.length === 1) {
          this.handleSelect(index, partialMatches[0])
          return
        }

        this.clearLevel(index)
      })
    },

    onInput(index, value) {
      const config = LEVEL_CONFIG[index]

      if (value === '') {
        this.clearLevel(index)
        this._activateDropdown(config.key)
        return
      }

      if (this.selected[config.key]) {
        this.selected[config.key] = null
        this._setChildOptions(index + 1, [])
        this.clearFrom(index + 1)
        this.syncTitlesCorrespondence()
        this.showIncompleteError = this.isSelectionIncomplete
        this.emitChanges()
      }
    },

    // ==========================================
    // NORMALIZE & SYNC
    // ==========================================
    normalizeTitlesCorrespondence(tc) {
      if (!tc) return
      const fields = ['fieldOfStudy', 'titleProgramme', 'specialization', 'subSpecialization', 'universityDepartment']
      fields.forEach(field => {
        if (!tc[field]) {
          tc[field] = { id: null, name: null, nameEn: null }
        } else if (!('nameEn' in tc[field])) {
          tc[field].nameEn = null
        }
      })
    },

    syncTitlesCorrespondence() {
      const tc = this.application.titlesCorrespondence

      LEVEL_CONFIG.forEach(level => {
        const item = this.selected[level.key]
        tc[level.tcKey] = item
          ? { id: item.id, name: item[level.fieldGr], nameEn: item[level.fieldEn] }
          : { id: null, name: null, nameEn: null }
      })

      const deepest = [...LEVEL_CONFIG].reverse().find(level => this.selected[level.key])
      if (deepest) {
        tc.universityDepartment = {
          id: this.selected[deepest.key].id,
          name: this._buildPath(level => level.fieldGr),
          nameEn: this._buildPath(level => level.fieldEn),
        }
      }
    },

    // ==========================================
    // RESTORE & RESET
    // ==========================================
    resetSelections() {
      LEVEL_CONFIG.forEach(level => {
        this.inputs[level.key]   = ''
        this.selected[level.key] = null
      })
      this.level2Options = []
      this.level3Options = []
      this.level4Options = []
      this.showIncompleteError = false
    },

    restoreSelection() {
      if (this.isLegacyApplication) return
      const tc = this.application.titlesCorrespondence
      if (!tc.fieldOfStudy?.id) {
        this.syncTitlesCorrespondence()
        return
      }

      const sources = [this.academicAreasAll, null, null, null]
      const tcKeys  = ['fieldOfStudy', 'titleProgramme', 'specialization', 'subSpecialization']

      for (let i = 0; i < LEVEL_CONFIG.length; i++) {
        const config = LEVEL_CONFIG[i]
        const source = sources[i]
        if (!source || !tc[tcKeys[i]]?.id) break

        const item = source.find(x => x.id === tc[tcKeys[i]].id)
        if (!item) break

        this.selected[config.key] = item
        this.inputs[config.key]   = this.isGreek ? item[config.fieldGr] : item[config.fieldEn]

        if (config.childrenKey) {
          const childOptions = item[config.childrenKey] || []
          this._setChildOptions(i + 1, childOptions)
          sources[i + 1] = childOptions
        }
      }

      this.syncTitlesCorrespondence()
      this.emitChanges()
    },

    emitChanges() {
      this.$emit('correspondenceValidEvent', !this.isSelectionIncomplete)
      this.$emit('titlesCorrespondenceChanged', this.application.titlesCorrespondence)
    },
  },

  watch: {
    checkFormTitles() {
      this.showIncompleteError = this.isSelectionIncomplete
      this.$emit('correspondenceValidEvent', !this.isSelectionIncomplete)
    },

    application: {
      handler(newVal, oldVal) {
        this.normalizeTitlesCorrespondence(newVal.titlesCorrespondence)
        if (newVal.id !== oldVal.id) {
          this.resetSelections()
          if (this.academicAreasAll?.length > 0) this.restoreSelection()
        }
      },
      deep: true,
    },

    academicAreasAll: {
      handler(newVal) {
        if (newVal?.length > 0) this.restoreSelection()
      },
      immediate: true,
    },

    '$i18n.locale'() {
      LEVEL_CONFIG.forEach(level => {
        const item = this.selected[level.key]
        if (item) {
          this.inputs[level.key] = this.isGreek ? item[level.fieldGr] : item[level.fieldEn]
        }
      })
    },
  },
}
</script>

<style lang="scss">
.correspondence-autocomplete {
  li {
    line-height: 1.5 !important;
    padding: 12px 15px !important;
    border-bottom: 1px solid #e8e8e8 !important;
    min-height: 45px !important;
    display: flex !important;
    align-items: center !important;
    transition: background-color 0.2s !important;

    &:last-child {
      border-bottom: none !important;
    }

    &:hover {
      background-color: #f5f7fa !important;
    }

    .value {
      text-overflow: clip !important;
      font-size: 14px !important;
      overflow: visible !important;
      color: #6d728b !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      line-height: 1.5 !important;
    }

    .link {
      text-overflow: clip !important;
      font-size: 14px !important;
      color: #33408a !important;
      white-space: normal !important;
      word-wrap: break-word !important;
      flex: 1 !important;
      line-height: 1.5 !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.selection-path {
  margin-top: 15px;
  padding: 10px;
  background-color: #f0f9ff;
  border-left: 4px solid #409eff;
  font-size: 14px;
  line-height: 1.6;
}

.selection-error {
  margin-top: 8px;
  color: #f56c6c;
  font-size: 13px;
  line-height: 1.5;

  i {
    margin-right: 4px;
  }
}

.el-autocomplete {
  width: 440px;
}
</style>
